import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField, Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, TablePagination } from "@mui/material";
import { Add } from "@mui/icons-material";
import { fetchPayments, fetchPaymentByCustomerChart } from "../../../services/customerModule/payments/paymentService";
// Customer-scoped payments come from the customerinfo router
// (/getcustomerpayments), which reads dbo.customerconvevent.
import { getcustomerpayments } from "../../../services/customerinfoModule/historyServices";
import { toCurrency } from "../../../utils/utils";
import { SIZES, SORT_ORDERS } from "../../../common/constants/commonConstants";
import { PAYMENT_STRINGS, PAYMENT_ERRORS, PAYMENT_FIELDS } from "../../../common/constants/customerModule/paymentConstants";
import AddPayment from "./AddPayment";
import CustomerDashPaymentChart from "../../dashboard/customersDash/charts/CustomerDashPaymentChart";
import TableSortLabel from '@mui/material/TableSortLabel';
import { getComparator, handleSort } from "../../../hooks/useTable";
import { useRegisterCsvExport } from "../customers/CsvExportContext.jsx";
import { PAYMENTS_COLUMNS } from "../customers/customerExportConfig.js";

// Customer-scoped rows come from dbo.customerconvevent, which has no
// currency or payment-method column but does carry fee / total /
// confirmation number. These are the sort keys for that column set —
// move them into PAYMENT_STRINGS / PAYMENT_FIELDS if you want them
// centralised with the rest.
const EVENT_FIELDS = {
	DATE: "paymentDate",
	TYPE: "paymentType",
	AMOUNT: "paymentAmount",
	FEE: "paymentFeeAmount",
	TOTAL: "totalAmount",
	CONFIRMATION: "confirmationNumber",
	CHANNEL: "channel",
};

const EVENT_LABELS = {
	FEE: "Fee",
	TOTAL: "Total",
	CONFIRMATION: "Confirmation #",
	CHANNEL: "Channel",
};

const Payments = ({ customerguid = null, isDash = false }) => {
	// A single customer's payments are event rows; the unscoped list is the
	// existing payments table. The two need different columns.
	const isCustomerView = Boolean(customerguid);

	const [payments, setPayments] = useState([]);
	const [search, setSearch] = useState("");

	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [showAddPayment, setShowAddPayment] = useState(false);

	// if viewing payments for a specific customer, disallow opening the AddPayment flow
	useEffect(() => {
		if (customerguid) setShowAddPayment(false);
	}, [customerguid]);

	const [chartData, setChartData] = useState([]);

	useEffect(() => {
		const fetchChart = async () => {
			try {
				const data = await fetchPaymentByCustomerChart(customerguid, isDash ? "Payment" : null);
				setChartData(data || []);
			} catch (error) {
				console.error("Error fetching payment chart:", error);
			}
		};

		if (isDash && customerguid) {
			fetchChart();
		}
	}, [customerguid, isDash]);

	// Customer view is a chronological event log, so it defaults to newest
	// first; the unscoped list keeps its A–Z by customer name.
	const [order, setOrder] = useState(isCustomerView ? SORT_ORDERS.DESCENDING : SORT_ORDERS.ASCENDING);
	const [orderBy, setOrderBy] = useState(isCustomerView ? EVENT_FIELDS.DATE : PAYMENT_FIELDS.CUSTOMER_NAME);

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			try {
				setIsLoading(true);
				let data = [];
				if (customerguid) {
					data = await getcustomerpayments({
						customerguid,
						paymentType: isDash ? "Payment" : null,
					});
				} else {
					data = await fetchPayments();
				}
				if (mounted) setPayments(data || []);
			} catch (error) {
				// keep payments empty on error
				console.error(PAYMENT_ERRORS.FETCH_PAYMENTS_ERROR, error);
				if (mounted) setPayments([]);
			} finally {
				if (mounted) setIsLoading(false);
			}
		};
		load();
		return () => { mounted = false; };
	}, [customerguid, isDash]);

	const formatDate = (value) =>
		value ? new Date(value).toLocaleDateString('en-US') : PAYMENT_STRINGS.NOT_AVAILABLE;

	// Events carry a meaningful time-of-day, so the customer view shows it.
	const formatDateTime = (value) => {
		if (!value) return PAYMENT_STRINGS.NOT_AVAILABLE;
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return PAYMENT_STRINGS.NOT_AVAILABLE;
		return `${date.toLocaleDateString('en-US')} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
	};

	const formatMoney = (value) => {
		if (value === null || value === undefined || value === "") return PAYMENT_STRINGS.NOT_AVAILABLE;
		const num = Number(value);
		if (Number.isNaN(num)) return PAYMENT_STRINGS.NOT_AVAILABLE;
		return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	// No search box in customer view, so skip the filter pass there entirely.
	const paymentsToDisplay = useMemo(() => {
		const q = isCustomerView ? "" : (search || "").toLowerCase().trim();
		const filtered = q
			? payments.filter((p) => {
				const dateStr = p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-US') : "";
				return (
					dateStr.toLowerCase().includes(q) ||
					(p.customerId || "").toString().toLowerCase().includes(q) ||
					(p.customerName || "").toLowerCase().includes(q)
				);
			})
			: payments;
		const sorted = [...filtered].sort(getComparator(order, orderBy));
		return sorted;
	}, [payments, search, order, orderBy, isCustomerView]);

	// Exports every sorted row, not just the current page. Only the
	// customer-scoped column set is registered — the unscoped list has
	// different columns and lives outside the customer tabs anyway.
	useRegisterCsvExport({
		name: "payments",
		columns: PAYMENTS_COLUMNS,
		rows: paymentsToDisplay,
		enabled: isCustomerView && !isLoading,
	});

	const pagedPayments = useMemo(() => {
		const start = page * rowsPerPage;
		return paymentsToDisplay.slice(start, start + rowsPerPage);
	}, [paymentsToDisplay, page, rowsPerPage]);

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	const sortableHeader = (field, label) => (
		<TableCell align="center">
			<TableSortLabel
				active={orderBy === field}
				direction={orderBy === field ? order : SORT_ORDERS.ASCENDING}
				onClick={() => handleRequestSort(field)}
			>
				{label}
			</TableSortLabel>
		</TableCell>
	);

	useEffect(() => { setPage(0); }, [search]);

	if (showAddPayment) {
		return <AddPayment onBack={() => setShowAddPayment(false)} />;
	}

	return (
		<Container width="xl" disableGutters>
			{/* Search / Add are for the unscoped payments list only — a single
			    customer's event log is short enough to read directly. */}
			{!isCustomerView && (
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid size={SIZES.GROW}>
						<TextField
							fullWidth
							size={SIZES.SMALL}
							value={search}
							variant="outlined"
							placeholder={PAYMENT_STRINGS.SEARCH_PLACEHOLDER}
							onChange={e => setSearch(e.target.value)}
						/>
					</Grid>
					<Grid size={SIZES.AUTO}>
						<Button startIcon={<Add />} variant="contained" fullWidth onClick={() => setShowAddPayment(true)}>
							{PAYMENT_STRINGS.ADD_PAYMENT}
						</Button>
					</Grid>
				</Grid>
			)}
			{isDash && (
				<CustomerDashPaymentChart
					rows={chartData}
					title="Payment Volume"
					label="Payments"
					isCurrency={true}
				/>
			)}

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						{isCustomerView ? (
							<TableRow>
								{sortableHeader(EVENT_FIELDS.DATE, PAYMENT_STRINGS.DATE)}
								{sortableHeader(EVENT_FIELDS.TYPE, PAYMENT_STRINGS.TYPE)}
								{sortableHeader(EVENT_FIELDS.AMOUNT, PAYMENT_STRINGS.AMOUNT)}
								{sortableHeader(EVENT_FIELDS.FEE, EVENT_LABELS.FEE)}
								{sortableHeader(EVENT_FIELDS.TOTAL, EVENT_LABELS.TOTAL)}
								<TableCell align="center">{EVENT_LABELS.CONFIRMATION}</TableCell>
								{sortableHeader(EVENT_FIELDS.CHANNEL, EVENT_LABELS.CHANNEL)}
							</TableRow>
						) : (
							<TableRow>
								<TableCell align="center">{PAYMENT_STRINGS.DATE}</TableCell>
								{sortableHeader(PAYMENT_FIELDS.PAYMENT_TYPE, PAYMENT_STRINGS.TYPE)}
								<TableCell align="center">{PAYMENT_STRINGS.CUSTOMER_ID}</TableCell>
								{sortableHeader(PAYMENT_FIELDS.CUSTOMER_NAME, PAYMENT_STRINGS.CUSTOMER)}
								{sortableHeader(PAYMENT_FIELDS.CURRENCY, PAYMENT_STRINGS.CURRENCY)}
								{sortableHeader(PAYMENT_FIELDS.PAYMENT_AMOUNT, PAYMENT_STRINGS.AMOUNT)}
								{sortableHeader(PAYMENT_FIELDS.PAYMENT_METHOD, PAYMENT_STRINGS.PAYMENT_METHOD)}
							</TableRow>
						)}
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									<LinearProgress />
								</TableCell>
							</TableRow>
						) : pagedPayments.length > 0 ? (
							pagedPayments.map((p, index) => (
								<TableRow key={p.id ?? `${p.paymentDate}-${index}`}>
									{isCustomerView ? (
										<>
											<TableCell align="center">{formatDateTime(p.paymentDate)}</TableCell>
											<TableCell align="center">{p.paymentType || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{formatMoney(p.paymentAmount)}</TableCell>
											<TableCell align="center">{formatMoney(p.paymentFeeAmount)}</TableCell>
											<TableCell align="center">{formatMoney(p.totalAmount)}</TableCell>
											<TableCell align="center">{p.confirmationNumber || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.channel || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
										</>
									) : (
										<>
											<TableCell align="center">{formatDate(p.paymentDate)}</TableCell>
											<TableCell align="center">{p.paymentType || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.customerId || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.customerName || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.currency || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.paymentAmount != null ? p.paymentAmount : PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
											<TableCell align="center">{p.paymentMethod || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
										</>
									)}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={7} align="center">
									{PAYMENT_STRINGS.NO_PAYMENTS}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				page={page}
				rowsPerPage={rowsPerPage}
				count={paymentsToDisplay.length}
				rowsPerPageOptions={[10, 25, 50]}
				onPageChange={(event, newPage) => setPage(newPage)}
				onRowsPerPageChange={(event) => {
					setPage(0);
					setRowsPerPage(parseInt(event.target.value, 10));
				}}
			/>
		</Container>
	);
};

export default Payments;